import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTabPanelActive } from 'screens/Main/tabs/MyCards/components/TabbedView/components/TabPanel/TabPanel';
import MobileSheet from 'components/MobileSheet';
import { useIsMobile } from 'hooks/useIsMobile';
import { ENTRY_DEFAULTS_COMPACT_BELOW_PX } from 'constants/entryDefaults';
import { EntrySummary } from 'utils/entryDefaults';
import './EntryDefaults.css';

const POPOVER_WIDTH_PX = 340;
/** Field widths below which the pill drops its label, then abbreviates. */
const FIELD_NO_LABEL_BELOW_PX = 480;
const FIELD_SHORT_BELOW_PX = 380;

interface EntryDefaultsPillProps {
  /** "Adding as" (Have List) or "Accepting" (Want List). */
  label: string;
  summary: EntrySummary;
  isStock: boolean;
  /** Editor heading, e.g. "New cards are added as". */
  title: string;
  footer: string;
  onReset: () => void;
  /** Called when the editor closes, so the caller can put focus back in Card name. */
  onDone?: () => void;
  /** The editor's controls. */
  children: React.ReactNode;
}

/**
 * The pill inside the Card name field and the editor it opens: a popover
 * anchored under the pill on desktop, a bottom sheet below 640px.
 */
export const EntryDefaultsPill: React.FC<EntryDefaultsPillProps> = ({
  label,
  summary,
  isStock,
  title,
  footer,
  onReset,
  onDone,
  children,
}) => {
  const compact = useIsMobile(ENTRY_DEFAULTS_COMPACT_BELOW_PX - 1);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const close = () => {
    setOpen(false);
    onDone?.();
  };

  // The pill must leave room to type, so it follows the width of the field it
  // sits in: the label goes first, then the summary abbreviates. Below 640px it
  // is always the abbreviated form.
  const [fieldWidth, setFieldWidth] = useState<number | null>(null);
  useEffect(() => {
    const field = rootRef.current?.closest('.text-input-field');
    if (!field || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => setFieldWidth(entry.contentRect.width));
    observer.observe(field);
    return () => observer.disconnect();
  }, []);
  const density: 'full' | 'no-label' | 'short' =
    compact || (fieldWidth !== null && fieldWidth < FIELD_SHORT_BELOW_PX)
      ? 'short'
      : fieldWidth !== null && fieldWidth < FIELD_NO_LABEL_BELOW_PX
        ? 'no-label'
        : 'full';

  // The popover hangs from the pill's right edge. In a narrow field that would
  // push it past the field's left edge (under the page's left rail), so there it
  // lines up with the field's left edge instead.
  const [alignLeftPx, setAlignLeftPx] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!open || compact) return;
    const root = rootRef.current;
    const field = root?.closest('.text-input-field');
    if (!root || !field) return;
    const rootRect = root.getBoundingClientRect();
    const fieldRect = field.getBoundingClientRect();
    setAlignLeftPx(rootRect.right - POPOVER_WIDTH_PX < fieldRect.left ? fieldRect.left - rootRect.left : null);
  }, [open, compact]);

  // The field often sits low on the page and the editor is taller than the space
  // under it, so bring the whole popover into view when it opens.
  const popoverRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open && !compact) popoverRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [open, compact]);

  // Desktop popover: outside click and Escape close it. The sheet handles its own.
  // Document-level listeners only while this pill's tab is visible.
  const active = useTabPanelActive();
  useEffect(() => {
    if (!open || compact || !active) return undefined;
    const onMouseDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        onDone?.();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, compact, onDone, active]);

  const editor = (
    <div className="entry-defaults-editor">
      <div className="entry-defaults-editor__header">
        <span className="entry-defaults-editor__title">{title}</span>
        <button type="button" className="entry-defaults-editor__reset" onClick={onReset} disabled={isStock}>
          Reset
        </button>
      </div>
      {children}
      <p className="entry-defaults-editor__footer">{footer}</p>
    </div>
  );

  return (
    <div className="entry-defaults" ref={rootRef}>
      <button
        type="button"
        className={`entry-defaults-pill ${isStock ? '' : 'entry-defaults-pill--changed'}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${label}: ${summary.full}. Change entry defaults`}
        onClick={() => setOpen((o) => !o)}
      >
        {density === 'full' && <span className="entry-defaults-pill__label">{label}</span>}
        <span className="entry-defaults-pill__summary">
          {density === 'short' ? summary.short : summary.full}
        </span>
        {/* The same 12px chevron as the Printing / View / Sort selectors beside the field. */}
        <svg
          className={`entry-defaults-pill__chevron ${open ? 'open' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && !compact && (
        <div
          className="entry-defaults-popover"
          ref={popoverRef}
          role="dialog"
          aria-label={title}
          style={alignLeftPx === null ? undefined : { left: alignLeftPx, right: 'auto' }}
        >
          {editor}
        </div>
      )}
      {open && compact && (
        <MobileSheet ariaLabel={title} onClose={close} applyLabel="Done" onApply={close}>
          {editor}
        </MobileSheet>
      )}
    </div>
  );
};

export const EntryDefaultsSection: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="entry-defaults-section">
    <span className="entry-defaults-section__label">{label}</span>
    {children}
  </div>
);

export default EntryDefaultsPill;
